/**
 * Relatórios > Financeiro > Meios de Pagamento.
 *
 * Vendas pagas mês a mês por faixa — cartão à vista, 2 a 6x, 7 a 12x e PIX —
 * consolidado ou por empresa, em barras empilhadas e tabela; num segundo
 * painel, as bandeiras de cartão × número de parcelas. PDF e Excel são
 * gerados no back com os mesmos filtros.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BarChart3, Calendar, CreditCard, FileSpreadsheet, FileText } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { clsx } from 'clsx';
import financialReportsService from '@services/financialReportsService';
import type { PaymentMethodsReport, PaymentMethodsTotals } from '@/types/financialReports';
import { formatCurrencyBRL } from '@pages/financial/financialUtils';
import {
  ErrorBox,
  LoadingSpinner,
  SummaryCard,
  labelCls,
  primaryBtnCls,
  secondaryBtnCls,
  selectCls,
} from '@pages/financial/financialShared';
import { ChartTooltip, CompanySelect, getMonthOptions, getYearOptions, kFormatter, useCompanies } from './financialReportsShared';
import { formatPct } from './financialReportsUtils';
import {
  PAYMENT_METHOD_SERIES,
  brandInstallmentColumns,
  buildPaymentChartData,
  defaultPaymentPeriod,
  monthTotalsFor,
  paymentMonthLabel,
  sharePct,
  toYearMonth,
} from './paymentMethodsReport';

type Panel = 'months' | 'brands';

const pctOrDash = (part: number, total: number) => {
  const pct = sharePct(part, total);
  return pct == null ? '—' : formatPct(pct);
};

/** Tabela mês × faixa (com Outros, Total, Pedidos, linha de total e de %). */
const MonthTable: React.FC<{
  title: string;
  rows: { label: string; totals: PaymentMethodsTotals }[];
  total: PaymentMethodsTotals;
}> = ({ title, rows, total }) => (
  <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
    <div className="px-4 py-3 border-b border-slate-200">
      <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
    </div>
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50 text-slate-600">
          <tr>
            <th className="px-3 py-2 text-left font-medium">Mês</th>
            {PAYMENT_METHOD_SERIES.map((s) => (
              <th key={s.key} className="px-3 py-2 text-right font-medium whitespace-nowrap">
                <span className="inline-block w-2.5 h-2.5 rounded-sm mr-1.5 align-middle" style={{ backgroundColor: s.color }} />
                {s.label}
              </th>
            ))}
            <th className="px-3 py-2 text-right font-medium">Outros</th>
            <th className="px-3 py-2 text-right font-medium">Total</th>
            <th className="px-3 py-2 text-right font-medium">Pedidos</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r) => (
            <tr key={r.label} className="hover:bg-slate-50">
              <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{r.label}</td>
              {PAYMENT_METHOD_SERIES.map((s) => (
                <td key={s.key} className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{formatCurrencyBRL(r.totals[s.key])}</td>
              ))}
              <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap text-slate-500">{formatCurrencyBRL(r.totals.other)}</td>
              <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap font-medium">{formatCurrencyBRL(r.totals.total)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{r.totals.orders}</td>
            </tr>
          ))}
        </tbody>
        <tfoot className="bg-slate-50">
          <tr className="font-semibold text-slate-800">
            <td className="px-3 py-2">Total</td>
            {PAYMENT_METHOD_SERIES.map((s) => (
              <td key={s.key} className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{formatCurrencyBRL(total[s.key])}</td>
            ))}
            <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{formatCurrencyBRL(total.other)}</td>
            <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{formatCurrencyBRL(total.total)}</td>
            <td className="px-3 py-2 text-right tabular-nums">{total.orders}</td>
          </tr>
          <tr className="text-xs text-slate-500 italic">
            <td className="px-3 py-1.5">% do total</td>
            {PAYMENT_METHOD_SERIES.map((s) => (
              <td key={s.key} className="px-3 py-1.5 text-right">{pctOrDash(total[s.key], total.total)}</td>
            ))}
            <td className="px-3 py-1.5 text-right">{pctOrDash(total.other, total.total)}</td>
            <td className="px-3 py-1.5" colSpan={2} />
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
);

export const PaymentMethodsReportView: React.FC = () => {
  const { companies, companiesError } = useCompanies();
  const [period, setPeriod] = useState(defaultPaymentPeriod);
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [panel, setPanel] = useState<Panel>('months');
  const [report, setReport] = useState<PaymentMethodsReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState<'pdf' | 'excel' | null>(null);

  const dateFrom = toYearMonth(period.startYear, period.startMonth);
  const dateTo = toYearMonth(period.endYear, period.endMonth);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setReport(await financialReportsService.getPaymentMethodsReport(dateFrom, dateTo, companyId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar o relatório');
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, companyId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = async (kind: 'pdf' | 'excel') => {
    setExporting(kind);
    setError(null);
    try {
      if (kind === 'pdf') await financialReportsService.downloadPaymentMethodsPdf(dateFrom, dateTo, companyId);
      else await financialReportsService.downloadPaymentMethodsExcel(dateFrom, dateTo, companyId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao exportar');
    } finally {
      setExporting(null);
    }
  };

  const chartData = useMemo(() => (report ? buildPaymentChartData(report.months) : []), [report]);
  const brandCols = useMemo(() => brandInstallmentColumns(report?.brandTotals), [report]);
  const scopeLabel =
    companyId == null
      ? 'Consolidado (todas as empresas)'
      : companies.find((c) => c.company_id === companyId)?.name ?? `Empresa ${companyId}`;

  const monthOptions = getMonthOptions();
  const yearOptions = getYearOptions();
  const periodSelect = (prefix: 'start' | 'end', label: string) => (
    <div className="w-full sm:w-auto">
      <label className={labelCls}>{label}</label>
      <div className="flex gap-2">
        <select
          className={`${selectCls} w-full sm:w-24`}
          value={period[`${prefix}Month`]}
          onChange={(e) => setPeriod((p) => ({ ...p, [`${prefix}Month`]: Number(e.target.value) }))}
        >
          {monthOptions.map((m) => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </select>
        <select
          className={`${selectCls} w-full sm:w-24`}
          value={period[`${prefix}Year`]}
          onChange={(e) => setPeriod((p) => ({ ...p, [`${prefix}Year`]: Number(e.target.value) }))}
        >
          {yearOptions.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Filtros */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-4">
          {periodSelect('start', 'Início')}
          {periodSelect('end', 'Fim')}
          <div className="w-full sm:w-auto">
            <label className={labelCls}>Empresa</label>
            <CompanySelect companies={companies} value={companyId} onChange={setCompanyId} />
          </div>
          <button onClick={fetchData} disabled={loading} className={`${primaryBtnCls} w-full sm:w-auto justify-center`}>
            <Calendar size={16} />
            {loading ? 'Carregando...' : 'Atualizar'}
          </button>
          <button
            onClick={() => handleExport('excel')}
            disabled={exporting != null || loading || !report}
            className={`${secondaryBtnCls} w-full sm:w-auto justify-center`}
          >
            <FileSpreadsheet size={16} />
            {exporting === 'excel' ? 'Exportando...' : 'Exportar Excel'}
          </button>
          <button
            onClick={() => handleExport('pdf')}
            disabled={exporting != null || loading || !report}
            className={`${secondaryBtnCls} w-full sm:w-auto justify-center`}
          >
            <FileText size={16} />
            {exporting === 'pdf' ? 'Gerando...' : 'Imprimir PDF'}
          </button>
        </div>
        <p className="text-xs text-slate-500 mt-3">
          Pedidos de venda pagos, pelo mês da data do pedido. Valor = soma das parcelas (inclui frete e juros);
          Outros = boleto e débito. Período de até 24 meses.
        </p>
      </div>

      {companiesError && <ErrorBox message={companiesError} />}
      {error && <ErrorBox message={error} onRetry={fetchData} />}

      {/* Painéis */}
      <div className="inline-flex w-full sm:w-auto rounded-lg border border-slate-300 overflow-hidden">
        {([
          ['months', 'Mês a mês', <BarChart3 key="i" size={16} />],
          ['brands', 'Por bandeira', <CreditCard key="i" size={16} />],
        ] as [Panel, string, React.ReactNode][]).map(([id, label, icon]) => (
          <button
            key={id}
            type="button"
            onClick={() => setPanel(id)}
            className={clsx(
              'flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium transition-colors',
              panel === id ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50',
            )}
          >
            {icon}
            {label}
          </button>
        ))}
      </div>

      {loading && !report ? (
        <LoadingSpinner />
      ) : report ? (
        panel === 'months' ? (
          <>
            {/* Resumo do período */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              {PAYMENT_METHOD_SERIES.map((s) => (
                <SummaryCard
                  key={s.key}
                  label={s.label}
                  value={formatCurrencyBRL(report.totals[s.key])}
                  hint={pctOrDash(report.totals[s.key], report.totals.total) + ' do total'}
                />
              ))}
              <SummaryCard
                label="Total do período"
                value={formatCurrencyBRL(report.totals.total)}
                hint={`${report.totals.orders} pedidos`}
              />
            </div>

            {/* Gráfico empilhado */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
              <p className="text-sm font-medium text-slate-600 mb-2">Volume mensal por meio de pagamento — {scopeLabel}</p>
              <div className="h-80 sm:h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#64748b' }} />
                    <YAxis tickFormatter={kFormatter} tick={{ fontSize: 12, fill: '#64748b' }} width={48} />
                    <Tooltip content={<ChartTooltip showTotal totalLabel="Total do mês" />} />
                    <Legend />
                    {PAYMENT_METHOD_SERIES.map((s) => (
                      <Bar key={s.key} stackId="pay" dataKey={s.key} name={s.label} fill={s.color} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <MonthTable
              title={scopeLabel}
              rows={report.months.map((m) => ({ label: paymentMonthLabel(m.month), totals: m.totals }))}
              total={report.totals}
            />
            {companyId == null &&
              report.companyTotals.length > 1 &&
              report.companyTotals.map((c) => (
                <MonthTable
                  key={c.companyId}
                  title={c.companyName}
                  rows={report.months.map((m) => ({ label: paymentMonthLabel(m.month), totals: monthTotalsFor(m, c.companyId) }))}
                  total={c.totals}
                />
              ))}
          </>
        ) : (
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
            <div className="px-4 py-3 border-b border-slate-200">
              <h3 className="text-sm font-semibold text-slate-700">Bandeiras × parcelas — quantidade de pedidos ({scopeLabel})</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Bandeira</th>
                    {brandCols.map((n) => (
                      <th key={n} className="px-3 py-2 text-right font-medium">{n}x</th>
                    ))}
                    <th className="px-3 py-2 text-right font-medium">Pedidos</th>
                    <th className="px-3 py-2 text-right font-medium">Valor</th>
                    <th className="px-3 py-2 text-right font-medium">% do valor</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {report.brands.length === 0 ? (
                    <tr>
                      <td colSpan={brandCols.length + 4} className="px-3 py-8 text-center text-slate-500">
                        Nenhuma venda com cartão no período
                      </td>
                    </tr>
                  ) : (
                    report.brands.map((b) => (
                      <tr key={b.brand} className="hover:bg-slate-50">
                        <td className={clsx('px-3 py-2', b.brand === 'Não identificada' ? 'text-slate-500 italic' : 'text-slate-700 font-medium')}>
                          {b.brand}
                        </td>
                        {brandCols.map((n) => (
                          <td key={n} className={clsx('px-3 py-2 text-right tabular-nums', !b.counts[n - 1] && 'text-slate-300')}>
                            {b.counts[n - 1] ?? 0}
                          </td>
                        ))}
                        <td className="px-3 py-2 text-right tabular-nums font-medium">{b.orders}</td>
                        <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{formatCurrencyBRL(b.amount)}</td>
                        <td className="px-3 py-2 text-right text-slate-500">{pctOrDash(b.amount, report.brandTotals.amount)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
                <tfoot className="bg-slate-50">
                  <tr className="font-semibold text-slate-800">
                    <td className="px-3 py-2">Total</td>
                    {brandCols.map((n) => (
                      <td key={n} className="px-3 py-2 text-right tabular-nums">{report.brandTotals.counts[n - 1] ?? 0}</td>
                    ))}
                    <td className="px-3 py-2 text-right tabular-nums">{report.brandTotals.orders}</td>
                    <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{formatCurrencyBRL(report.brandTotals.amount)}</td>
                    <td className="px-3 py-2" />
                  </tr>
                </tfoot>
              </table>
            </div>
            <p className="text-xs text-slate-500 px-4 py-3 border-t border-slate-100">
              Bandeira do pagamento capturado no PaymentHub; parcelas do pedido. "Não identificada" = venda de cartão sem
              pagamento no hub (PDV e pedidos antigos). Colunas de parcelas sem pedido ficam ocultas.
            </p>
          </div>
        )
      ) : null}
    </div>
  );
};

export default PaymentMethodsReportView;
